import React, { FunctionComponent, useMemo } from "react"
import {
  Slide,
  Box,
  Paper,
  IconButton,
  makeStyles,
  Theme,
} from "@material-ui/core"
import { CloseRounded as CloseRoundedIcon } from "@material-ui/icons"

import { SidebarHeader } from "../common/SidebarHeader"
import { FeatureForm, applyEditsResult } from "./FeatureForm"
import { CompletionIndicator } from "../common/CompletionIndicator"
import { parseEsriFields } from "./fieldHelpers"
import { NEW_GRAPHIC_OBJECTID } from "../feature-layer/AddFeatureButton"

/** FeatureFormModal component props */
interface FeatureFormModalProps {
  featureLayer?: __esri.FeatureLayer
  feature?: __esri.Graphic
  onClose?: () => void
  beforeSave?: () => void
  onSuccess?: (result: applyEditsResult) => void
}

/** Scoped @material-ui/core stylesheet */
const useStyles = makeStyles<Theme>(theme => ({
  root: {
    position: "absolute",
    right: theme.spacing(2),
    top: theme.spacing(2),
    bottom: theme.spacing(10),
    width: 375 - theme.spacing(4),
    [theme.breakpoints.up("sm")]: {
      width: 475,
    },
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
}))

export const FeatureFormModal: FunctionComponent<FeatureFormModalProps> = ({
  featureLayer,
  feature,
  onClose,
  beforeSave,
  onSuccess,
}) => {
  /** Get scoped CSS classes */
  const classes = useStyles()

  /** Get schema fields for completion indicators */
  const fields = useMemo(
    () => featureLayer && parseEsriFields(featureLayer?.fields),
    [featureLayer]
  )

  return (
    <Slide in={Boolean(feature)} direction="left">
      <Box className={classes.root}>
        <Paper className={classes.paper}>
          {feature && (
            <>
              <SidebarHeader
                primary={
                  feature.attributes.OBJECTID === NEW_GRAPHIC_OBJECTID
                    ? "New feature"
                    : featureLayer?.displayField &&
                      feature?.attributes[featureLayer?.displayField]
                }
                left={
                  <CompletionIndicator
                    fields={fields}
                    attributes={feature.attributes}
                    mr={1}
                  />
                }
                secondary={
                  typeof feature?.attributes.OBJECTID === "number" &&
                  "ID: " + feature?.attributes.OBJECTID
                }
                right={
                  <IconButton edge="end" onClick={onClose}>
                    <CloseRoundedIcon />
                  </IconButton>
                }
              />
              <Box flexGrow={1} overflow="hidden">
                <FeatureForm
                  featureLayer={featureLayer}
                  feature={feature}
                  onCancel={onClose}
                  onSuccess={onSuccess}
                  beforeSave={beforeSave}
                />
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Slide>
  )
}
